// Organization Hierarchy — API layer.
// Antigravity: confirm the real default export of the shared axios client
// (grep an existing working feature, e.g. features/shopfloor/api.ts, for the
// exact import line) before wiring — this file assumes a default export
// named `apiClient` with the shared instance's interceptors (auth, tenant
// header) already attached. Per AM-049 §3, whatever attaches X-Tenant-ID
// today must be checked against AM-049's conflict rule — if this client
// sends a header that doesn't match the active JWT's tenant, every request
// through this file will start failing with 400 TENANT_RESOLUTION_CONFLICT
// once AM-049 is enforced. Fix the header source (or stop sending it) as
// part of Phase 4 of the runbook, not silently in this file.
import { axiosInstance as apiClient } from "@/api/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApiErrorShape,
  Organization,
  OrganizationInvitation,
  OrganizationMembership,
} from "./types";

const BASE = "/api/v1/organizations";

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function results<T>(data: Paginated<T> | T[]): T[] {
  return Array.isArray(data) ? data : data.results ?? [];
}

export function isApiError(err: unknown): err is Error & { response: { status: number; data: ApiErrorShape } } {
  return err instanceof Error && "response" in err;
}

// -- Organizations --

export function useOrganizationsList(rootsOnly = false) {
  return useQuery({
    queryKey: ["organizations", { rootsOnly }],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<Organization>>(`${BASE}/`, {
        params: rootsOnly ? { roots_only: "true" } : undefined,
      });
      return results(data);
    },
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      code: string;
      name: string;
      organization_type: string;
      legal_name?: string;
      registration_number?: string;
      tax_identifier?: string;
    }) => {
      const { data } = await apiClient.post<Organization>(`${BASE}/`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["organizations"] }),
  });
}

// AM-025 Addendum §8: PATCH-only. Only writable fields belong in `payload` —
// sending `code`, `organization_type`, `status`, `tenant_id`, or `id`
// returns 400 by design; don't spread the whole Organization object here.
export interface OrganizationPatchPayload {
  name?: string;
  legal_name?: string;
  registration_number?: string;
  tax_identifier?: string;
}

export function useUpdateOrganization(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: OrganizationPatchPayload) => {
      const { data } = await apiClient.patch<Organization>(`${BASE}/${organizationId}/`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["organizations"] });
      qc.invalidateQueries({ queryKey: ["organizations", organizationId] });
    },
  });
}

export function useArchiveOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: string) => {
      const { data } = await apiClient.post<Organization>(`${BASE}/${organizationId}/archive/`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["organizations"] }),
  });
}

// -- Hierarchy (children) --

export function useOrganizationChildren(organizationId: string | null) {
  return useQuery({
    queryKey: ["organizations", organizationId, "children"],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<Organization> | Organization[]>(
        `${BASE}/${organizationId}/children/`,
      );
      return results(data);
    },
  });
}

export function useAddChildOrganization(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (childId: string) => {
      const { data } = await apiClient.post<Organization>(`${BASE}/${organizationId}/children/`, {
        child_id: childId,
      });
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "children"] }),
  });
}

export function useRemoveChildOrganization(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (childId: string) => {
      await apiClient.delete(`${BASE}/${organizationId}/children/${childId}/`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "children"] }),
  });
}

// -- Memberships --

export function useOrganizationMemberships(organizationId: string | null) {
  return useQuery({
    queryKey: ["organizations", organizationId, "memberships"],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<OrganizationMembership> | OrganizationMembership[]>(
        `${BASE}/${organizationId}/memberships/`,
      );
      return results(data);
    },
  });
}

// AM-025 Addendum §9: user_id must belong to the same tenant and be active —
// the backend rejects anything else with a deliberately generic message
// ("User not found in this tenant.") to avoid cross-tenant enumeration; this
// layer does not attempt its own client-side validation of that.
export function useAddMembership(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { user_id: string; membership_type?: string }) => {
      const { data } = await apiClient.post<OrganizationMembership>(
        `${BASE}/${organizationId}/memberships/`,
        payload,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "memberships"] }),
  });
}

export function useRemoveMembership(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`${BASE}/${organizationId}/memberships/${userId}/`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "memberships"] }),
  });
}

// -- Invitations --

export function useOrganizationInvitations(organizationId: string | null) {
  return useQuery({
    queryKey: ["organizations", organizationId, "invitations"],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<OrganizationInvitation> | OrganizationInvitation[]>(
        `${BASE}/${organizationId}/invitations/`,
      );
      return results(data);
    },
  });
}

// AM-025 Addendum §10: `invited_by` is NEVER sent by the client — the
// backend always uses the authenticated user regardless of what's in the
// body. This mutation's payload type intentionally has no `invited_by`
// field so it's impossible to accidentally send one.
export function useSendInvitation(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string }) => {
      const { data } = await apiClient.post<OrganizationInvitation>(
        `${BASE}/${organizationId}/invitations/`,
        payload,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "invitations"] }),
  });
}

// AM-025 Addendum §10: accept/reject take no body at all — identity is
// always the authenticated session on the server side.
export function useAcceptInvitation(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data } = await apiClient.post<OrganizationInvitation>(
        `${BASE}/${organizationId}/invitations/${invitationId}/accept/`,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "invitations"] }),
  });
}

export function useRejectInvitation(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data } = await apiClient.post<OrganizationInvitation>(
        `${BASE}/${organizationId}/invitations/${invitationId}/reject/`,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["organizations", organizationId, "invitations"] }),
  });
}

export function useUsersList() {
  return useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data } = await apiClient.get<Paginated<{id: string; first_name?: string; last_name?: string; username: string; email: string}>>("/api/v1/users/");
      return results(data);
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { username: string; email: string; first_name?: string; last_name?: string; password?: string }) => {
      const { data } = await apiClient.post<{ id: string; username: string; email: string; first_name?: string; last_name?: string }>("/api/v1/users/", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
